/**
 * Zentrale Datums-/Zeit-Formatierung — die EINZIGE Stelle, an der Server-Zeitstempel
 * in die Zeitzone des Users umgerechnet werden.
 *
 * WARUM (siehe docs/timezone-utc-migration.md):
 * Das Backend liefert Zeitstempel fast durchgängig OHNE Zonen-Suffix (2.290 ×
 * `isoformat()`, davon nur 23 mit "Z"). `new Date("2026-07-28T21:48:24")` interpretiert
 * so einen String als LOKALZEIT DES BROWSERS — für einen Wiener User 2 h daneben, für
 * New York 6 h, für Singapur 8 h. Genau das blockiert internationale User.
 *
 * REGEL: Nackter Server-String := UTC. Explizite Zone (Z / ±HH:MM) gewinnt immer, damit
 * bereits korrekt markierte Endpoints (und Phase B) unverändert richtig bleiben.
 *
 * ⚠ ÜBERGANG: Es gibt im Backend noch 485 × `datetime.now()`, die naiv-LOKALE (Wiener)
 * Zeit schreiben. Deren Werte erscheinen mit diesem Helper vorübergehend 2 h zu spät,
 * bis Phase C sie auf UTC zieht. Vorher waren für Nicht-Wiener User ALLE Zeitstempel
 * falsch — der Helper ist also in jedem Fall die Verbesserung, nicht die Verschlechterung.
 */

// Modul-State: die aktive User-Zeitzone. Wird vom Auth-Kontext gesetzt (Profilfeld
// users.timezone); leer/ungesetzt => automatisch die Browser-Zone.
let _userTimezone = null;

// ── Performance (25.09.2026) ─────────────────────────────────────────────────
// Ein Intl.DateTimeFormat anzulegen ist TEUER (Locale-/Zonen-Daten laden) —
// formatieren mit einem fertigen Formatter dagegen billig. Frueher legte JEDER
// Aufruf einen neuen an, dazu je einen fuer browserTimezone() und die Zonen-
// Validierung. Im Communication Center (50 Zeilen x ~5 Datumsaufrufe pro
// Render) kostete das ~300 ms pro Klick auf eine Mail. Jetzt: Formatter pro
// (Locale, Optionen) gecacht, Browser-Zone und Zonen-/Locale-Pruefung ebenso.
const _fmtCache = new Map();

/**
 * Gecachter Intl.DateTimeFormat. Wirft wie der Konstruktor bei ungueltigen
 * Optionen (dann wird nichts gecacht) — Aufrufer fangen das wie bisher ab.
 */
export function getDateTimeFormat(locale, options = {}) {
  const key = `${locale || ''}|${JSON.stringify(options)}`;
  let f = _fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    if (_fmtCache.size > 300) _fmtCache.clear();
    _fmtCache.set(key, f);
  }
  return f;
}

let _browserTz = null;

/** Browser-Zone, defensiv (alte/exotische Engines liefern evtl. nichts). */
function browserTimezone() {
  if (_browserTz) return _browserTz;
  try {
    _browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    _browserTz = 'UTC';
  }
  return _browserTz;
}

// Ergebnis der Gueltigkeitspruefung je Zone/Locale (true/false).
const _validTz = new Map();
const _validLocale = new Map();

/**
 * Aktive Anzeige-Zeitzone: explizit gesetztes Profilfeld, sonst Browser.
 * Ungültige Zonen (Tippfehler im Profil, gelöschte IANA-Zone) fallen sauber zurück.
 */
export function getUserTimezone() {
  if (_userTimezone) {
    let ok = _validTz.get(_userTimezone);
    if (ok === undefined) {
      try {
        Intl.DateTimeFormat('en', { timeZone: _userTimezone });
        ok = true;
      } catch {
        /* ungültige Zone im Profil → Browser */
        ok = false;
      }
      _validTz.set(_userTimezone, ok);
    }
    if (ok) return _userTimezone;
  }
  return browserTimezone();
}

/** Setzt die User-Zeitzone (users.timezone). null/'' => automatisch (Browser). */
export function setUserTimezone(tz) {
  _userTimezone = tz || null;
}

// Modul-State: die aktive Anzeige-SPRACHE (bestimmt die Reihenfolge der Datumsteile).
let _userLocale = null;

/**
 * Anzeige-Locale fuer Datum/Uhrzeit. Default ist DEUTSCH — nicht die Browser-Sprache.
 *
 * WARUM (User-Befund 26.08.2026): Ohne locale-Angabe entscheidet die BROWSER-Sprache
 * ueber die Reihenfolge der Datumsteile. Auf einem englischen Browser wurde aus
 * { day:'2-digit', month:'2-digit', year:'numeric' } das amerikanische "08/26/2026"
 * statt "26.08.2026" — die Felder erzwingen nur die Stelligkeit, nicht die REIHENFOLGE.
 * XOFFI ist eine Anwendung mit deutscher Basissprache; das Datum muss deutsch aussehen,
 * auch wenn der Browser des Users auf Englisch steht.
 *
 * Ueberschreibbar via setUserLocale() (z.B. sobald ein User seine Sprache umstellt) und
 * pro Aufruf via { locale: '...' } — der Default bleibt Deutsch.
 */
export function getUserLocale() {
  if (_userLocale) {
    let ok = _validLocale.get(_userLocale);
    if (ok === undefined) {
      try {
        Intl.DateTimeFormat(_userLocale);
        ok = true;
      } catch {
        /* ungueltiges Sprachkuerzel => Default */
        ok = false;
      }
      _validLocale.set(_userLocale, ok);
    }
    if (ok) return _userLocale;
  }
  return 'de-DE';
}

/** Setzt die Anzeige-Sprache fuer Datum/Zeit. null/'' => zurueck auf Deutsch. */
export function setUserLocale(loc) {
  _userLocale = loc || null;
}

/** True, wenn der String bereits eine Zone trägt (Z, +02:00, -0500). */
function hasExplicitZone(s) {
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(s);
}

/**
 * Server-Zeitstempel → Date. Nackte Strings werden als UTC gelesen.
 * Akzeptiert Date, Millisekunden-Zahl und ISO-String. Gibt bei Unlesbarem null
 * zurück — Aufrufer sollen einen Platzhalter rendern statt "Invalid Date".
 */
export function parseServerDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  let s = String(value).trim();
  if (!s) return null;
  // MySQL-Stil "2026-07-28 21:48:24" → ISO
  if (s.includes(' ') && !s.includes('T')) s = s.replace(' ', 'T');
  // Nackt => UTC. Datums-only ("2026-07-28") bleibt unangetastet: JS liest das
  // bereits als UTC-Mitternacht, ein angehängtes Z würde daran nichts ändern.
  if (!hasExplicitZone(s) && s.includes('T')) s += 'Z';

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Reiner Kalender-Tag ("2026-07-28", DB-Typ `date` — 249 Spalten): Rechnungsdatum,
 * Geburtstag, Fälligkeit. Solche Werte dürfen NIEMALS zonenkonvertiert werden —
 * JS liest sie als UTC-Mitternacht, und in einer westlichen Zone (New York = UTC−4)
 * würde daraus der VORTAG. Diese Werte werden deshalb in UTC formatiert, damit der
 * Kalendertag stabil bleibt.
 */
function isDateOnly(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

function fmt(value, options, locale) {
  const d = parseServerDate(value);
  if (!d) return '';
  try {
    return getDateTimeFormat(locale || getUserLocale(), {
      timeZone: isDateOnly(value) ? 'UTC' : getUserTimezone(),
      // 24-Stunden-Format, IMMER — unabhängig von der Locale. Ohne dieses Flag
      // entscheidet die Browser-/UI-Sprache über das Ziffernblatt: bei en-US
      // stand im Chat "01:13 PM" statt "13:13" (User-Befund 2026-07-31, Wien).
      // Als Default gesetzt, damit ein Aufrufer es bei Bedarf noch überschreiben
      // kann — der Regelfall in dieser App ist aber 24 h.
      hour12: false,
      ...options,
    }).format(d);
  } catch {
    return '';
  }
}

/** Datum + Uhrzeit, z. B. "28.07.2026, 23:48". */
export function formatDateTime(value, { locale, ...options } = {}) {
  return fmt(value, { dateStyle: 'medium', timeStyle: 'short', ...options }, locale);
}

/**
 * Datum + Uhrzeit MIT Sekunden, z. B. "28.07.2026, 23:48:24" — für Logs/Verläufe,
 * wo die Sekunde die Reihenfolge erklärt (Send-Log: sent → delivered im selben Moment).
 *
 * Muss die Felder EINZELN setzen: `dateStyle`/`timeStyle` und `second` zusammen sind
 * laut Intl-Spec unzulässig und werfen — deshalb geht das nicht über formatDateTime().
 */
export function formatDateTimeSeconds(value, { locale, ...options } = {}) {
  return fmt(
    value,
    {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      ...options,
    },
    locale,
  );
}

/** Nur Datum, z. B. "28.07.2026". */
export function formatDate(value, { locale, ...options } = {}) {
  return fmt(value, { dateStyle: 'medium', ...options }, locale);
}

/** Nur Uhrzeit, z. B. "23:48". */
export function formatTime(value, { locale, ...options } = {}) {
  return fmt(value, { timeStyle: 'short', ...options }, locale);
}

/**
 * Relative Angabe ("vor 5 Minuten"). Fällt ab `absoluteAfterDays` auf das
 * absolute Datum zurück — "vor 143 Tagen" hilft niemandem.
 */
export function formatRelative(value, { locale, absoluteAfterDays = 7 } = {}) {
  const d = parseServerDate(value);
  if (!d) return '';
  const diffMs = d.getTime() - Date.now();
  const absDays = Math.abs(diffMs) / 86400000;
  if (absDays > absoluteAfterDays) return formatDate(value, { locale });

  const units = [
    ['day', 86400000],
    ['hour', 3600000],
    ['minute', 60000],
    ['second', 1000],
  ];
  try {
    const rtf = new Intl.RelativeTimeFormat(locale || getUserLocale(), { numeric: 'auto' });
    for (const [unit, ms] of units) {
      if (Math.abs(diffMs) >= ms || unit === 'second') {
        return rtf.format(Math.round(diffMs / ms), unit);
      }
    }
    return '';
  } catch {
    return formatDateTime(value, { locale });
  }
}

/** Millisekunden für Sortierung/Vergleiche (0 bei Unlesbarem → sortiert ans Ende). */
export function serverTimeMs(value) {
  const d = parseServerDate(value);
  return d ? d.getTime() : 0;
}
